import { useEffect, useMemo, useState } from 'react';
import { InputPresetOption, InputPresets } from './InputAdditions';
import { StyledTextField } from './StyledTextField';
import { Build } from '@mui/icons-material';
import { CenteredColumn } from '../Stacks';
import { Avatar, Chip, Stack, Typography } from '@mui/material';
import { StyledFormHeader } from './StyledFormHeader';

const regexPresets: InputPresetOption[] = [
  { value: '/^(.+)$/', label: 'any value' },
  { value: '/^(\\d{4})$/', label: 'digits' },
  { value: '/^(A|B)$/', label: 'keywords' },
  { value: '/^(\\d+)_([a-zA-Z0-9]+)$/', label: 'multiple' },
];

interface Props {
  label: string;
  value: string;
  /** associated elements will not show when undefined */
  replacement?: string;
  onChange: (value: string) => void;
  /** will only have an effect when replacement is not undefined */
  onChangeReplacement?: (replacement: string) => void;
  /** callback for regex validity change */
  onValid?: (valid: boolean) => void;
}

export default function RegexInput({
  label,
  replacement,
  value,
  onChange,
  onChangeReplacement,
  onValid,
}: Props): React.JSX.Element {
  const [isValid, setValid] = useState<boolean>(false);
  const [isTestOpen, setTestOpen] = useState<boolean>(false);

  const [currentRegex, setCurrentRegex] = useState<RegExp | undefined>();
  const [testInput, setTestInput] = useState<string>('');

  useEffect(() => {
    try {
      let inputValue = value.slice();
      if (inputValue.startsWith('/')) {
        inputValue = inputValue.slice(1);
      }
      if (inputValue.endsWith('/')) {
        inputValue = inputValue.slice(0, -1);
      }

      setCurrentRegex(new RegExp(inputValue));
      setValid(true);
    } catch (error) {
      setCurrentRegex(undefined);
      setValid(false);
    }
  }, [value]);

  useEffect(() => {
    if (onValid) {
      onValid(isValid);
    }
  }, [onValid, isValid]);

  useEffect(() => {
    if (value === '') {
      setTestOpen(false);
      setTestInput('');
    }
  }, [value]);

  const testResults: string[] = useMemo(() => {
    if (testInput === '' || currentRegex === undefined) {
      return [];
    }

    return testInput.match(currentRegex) ?? [];
  }, [currentRegex, testInput]);

  return (
    <CenteredColumn width={'100%'} spacing={1.5}>
      <InputPresets selected={value} onClick={(preset) => onChange(preset as string)} options={regexPresets}>
        <StyledTextField
          label={label}
          value={value}
          onChange={(event) => {
            onChange(event.target.value as string);
          }}
          error={!isValid}
          action={{
            icon: Build,
            onClick: () => {
              setTestOpen(!isTestOpen);
              if (isTestOpen) {
                setTestInput('');
              }
            },
            tooltip: 'Try',
            disabled: value === '',
          }}
          collapsible={{
            open: isTestOpen,
            content: (
              <CenteredColumn width={'100%'}>
                <StyledTextField
                  label={'Input'}
                  value={testInput}
                  onChange={(e) => {
                    setTestInput(e.target.value as string);
                  }}
                />
                <StyledFormHeader label={'Matches'} />
                <Stack spacing={1} direction={{ xs: 'column', md: 'row' }}>
                  {testResults.map((m, i) => {
                    const token = '$' + i.toString();
                    return (
                      <Chip
                        key={i}
                        variant={'outlined'}
                        label={
                          <Typography noWrap overflow={'ellipsis'} maxWidth={128}>
                            {m}
                          </Typography>
                        }
                        avatar={<Avatar>{token}</Avatar>}
                        sx={{ userSelect: 'none', overflow: '' }}
                      />
                    );
                  })}
                </Stack>
              </CenteredColumn>
            ),
          }}
        />
      </InputPresets>
      {replacement !== undefined && (
        <StyledTextField
          label={'Replacement'}
          value={replacement}
          onChange={(e) => {
            if (onChangeReplacement) {
              onChangeReplacement(e.target.value as string);
            }
          }}
        />
      )}
    </CenteredColumn>
  );
}
